import { useMemo } from 'react';
import { TreeSelect } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { categoriesApi } from '@api/categories';
import type { CategoryScope, CategoryTreeNode } from '../types';
import { buildCategoryTree } from '../utils/tree';

interface Props {
  /** Optional FE-only scope filter. Omit to query the full shared category pool. */
  scope?: CategoryScope;
  value?: string | string[];
  onChange?: (value: string | string[] | undefined) => void;
  multiple?: boolean;
  placeholder?: string;
  disabledIds?: string[];
  allowClear?: boolean;
  size?: 'small' | 'middle' | 'large';
  disabled?: boolean;
}

interface TreeSelectNode {
  value: string;
  title: string;
  disabled?: boolean;
  children?: TreeSelectNode[];
}

function toTreeNodes(nodes: CategoryTreeNode[], disabledIds: Set<string>): TreeSelectNode[] {
  return nodes.map((n) => ({
    value: n.id,
    title: n.name,
    disabled: disabledIds.has(n.id),
    children: n.children.length ? toTreeNodes(n.children, disabledIds) : undefined,
  }));
}

export function CategoryPicker({
  scope,
  value,
  onChange,
  multiple,
  placeholder,
  disabledIds,
  allowClear = true,
  size = 'middle',
  disabled,
}: Props) {
  const { t } = useTranslation('categories');
  const { data, isLoading } = useQuery({
    queryKey: ['categories', { scope: scope ?? 'all' }],
    queryFn: () => categoriesApi.list(scope ? { scope } : {}),
  });

  const treeData = useMemo<TreeSelectNode[]>(() => {
    const tree = buildCategoryTree(data?.items ?? []);
    return toTreeNodes(tree, new Set(disabledIds ?? []));
  }, [data, disabledIds]);

  return (
    <TreeSelect<string | string[]>
      treeData={treeData}
      value={value}
      onChange={(v) => onChange?.(v)}
      placeholder={placeholder ?? t('picker.placeholder')}
      treeDefaultExpandAll
      allowClear={allowClear}
      showSearch
      treeNodeFilterProp="title"
      size={size}
      disabled={disabled}
      loading={isLoading}
      multiple={multiple}
      style={{ width: '100%' }}
    />
  );
}
